// Port of SuiteModel/RevisionHeat.swift, `SearchIndex.rewriteVolume` and the app's LibraryModel+Heat.swift (spec §11.1):
// how much each scene has been rewritten over a period (words added + cut, from the fragments' word logs — the web has no
// SQLite index, so the logs are read directly, as the index projects them), levels 1–4 by quartile, folders taking their
// hottest scene; and cold scenes — neither opened on this device (a per-device opened-at map the caller keeps, as the Mac
// keeps `openedAt-<project>` in UserDefaults) nor modified anywhere within the cold period. Exact.
import type { ArrangementTreeNode } from '../model/arrangement.ts';
import type { FragmentRecord } from '../model/documents.ts';
import { writingDay } from '../edit/editProject.ts';
import type { OpenedProject } from '../project.ts';

/** The heatmap's periods (days) and their menu names (`heatmapPeriodName`). */
export const HEATMAP_PERIODS: readonly number[] = [7, 30, 90, 365];
export function heatmapPeriodName(days: number): string {
  switch (days) {
    case 7: return 'Last Week';
    case 30: return 'Last 30 Days';
    case 90: return 'Last 90 Days';
    case 365: return 'Last Year';
    default: return `Last ${days} Days`;
  }
}

/** The default cold period (`AppSettings.coldDays`; 0 turns it off). */
export const DEFAULT_COLD_DAYS = 90;

/** `RevisionHeat.levels`: 1–4 for fragments rewritten at all, by quartile of the nonzero volumes (the busiest quarter is
 *  4); untouched fragments are left out. */
export function heatLevels(volumes: Readonly<Record<string, number>>): Record<string, number> {
  const values = Object.values(volumes).filter((v) => v > 0).sort((a, b) => a - b);
  if (!values.length) return {};
  const n = values.length;
  const q = [values[Math.floor(n / 4)], values[Math.floor(n / 2)], values[Math.floor((n * 3) / 4)]];
  const out: Record<string, number> = {};
  for (const [id, v] of Object.entries(volumes)) {
    if (v <= 0) continue;
    out[id] = v >= q[2] ? 4 : v >= q[1] ? 3 : v >= q[0] ? 2 : 1;
  }
  return out;
}

/** `RevisionHeat.byNode`: a scene's own level, a folder's the hottest in it (so a hot chapter shows while collapsed). */
export function heatByNode(tree: readonly ArrangementTreeNode[], levels: Readonly<Record<string, number>>): Record<string, number> {
  const out: Record<string, number> = {};
  const walk = (t: ArrangementTreeNode): number => {
    const own = t.node.fragmentID !== undefined ? (levels[t.node.fragmentID] ?? 0) : 0;
    const level = t.children.reduce((m, c) => Math.max(m, walk(c)), own);
    if (level > 0) out[t.node.id] = level;
    return level;
  };
  tree.forEach(walk);
  return out;
}

/** `RevisionHeat.isCold`: neither opened on this device nor changed anywhere since `cutoff`. */
export function isCold(lastOpened: Date | null | undefined, modified: Date | null | undefined, cutoff: Date): boolean {
  const opened = lastOpened?.getTime() ?? -Infinity, changed = modified?.getTime() ?? -Infinity;
  return Math.max(opened, changed) < cutoff.getTime();
}

/** `rewriteVolume(projectID:since:)`: words added plus words cut per fragment on or after `sinceDay` (a `WritingDay`
 *  key), from each fragment's word log (`"<day> <device> +|-"` → running total; every device's keys summed). */
export function rewriteVolume(fragments: Iterable<Pick<FragmentRecord, 'id' | 'wordLog'>>, sinceDay: string): Record<string, number> {
  const out: Record<string, number> = {};
  for (const f of fragments) {
    let volume = 0;
    for (const [key, amount] of Object.entries(f.wordLog)) {
      const parts = key.split(' ').filter((p) => p !== '');
      if (parts.length !== 3 || !Number.isFinite(amount)) continue;
      if (parts[0] >= sinceDay) volume += amount;
    }
    if (volume > 0) out[f.id] = volume;
  }
  return out;
}

/** The first day a period of `days` counts from (`WritingDay.key(now − days)`, in the runtime's zone). */
export const heatSince = (days: number, now: Date = new Date()) => writingDay(new Date(now.getTime() - days * 86_400_000));

/** `refreshRevisionHeat`: the project's heat levels by fragment over the last `days`. */
export function revisionHeat(project: OpenedProject, days: number, now: Date = new Date()): Record<string, number> {
  const records = project.fragmentIds().map((id) => project.fragmentRecord(id)).filter((f): f is FragmentRecord => f !== null);
  return heatLevels(rewriteVolume(records, heatSince(days, now)));
}

/** `nodeHeat(in:)`: heat by node for an arrangement. */
export function nodeHeat(project: OpenedProject, arrangementID: string, levels: Readonly<Record<string, number>>): Record<string, number> {
  return Object.keys(levels).length ? heatByNode(project.arrangementTree(arrangementID), levels) : {};
}

/** `isCold(_:hasChildren:)`: a scene (not a folder) neither opened on this device (`openedAt`, by fragment id) nor
 *  changed anywhere within `coldDays` (0: never cold). */
export function isColdNode(project: OpenedProject, t: ArrangementTreeNode, openedAt: Readonly<Record<string, Date>>,
  coldDays: number = DEFAULT_COLD_DAYS, now: Date = new Date()): boolean {
  const id = t.node.fragmentID;
  if (coldDays <= 0 || t.node.isFolder || t.children.length > 0 || id === undefined) return false;
  const record = project.fragmentRecord(id);
  if (!record) return false;
  return isCold(openedAt[id], record.modifiedAt, new Date(now.getTime() - coldDays * 86_400_000));
}
